import { Department, DepartmentGroup } from "../domain/department";
import { User, Profession, UserSelectionInformation } from "../domain/user";
import { IConfiguration } from "../domain/configuration";
import { UserAuthority } from "../domain/authentication";
import { ILaunchedInformation } from "../system";
import { WebStorageKeys } from "./WebStorageKeys";

export class SessionStorageAccessor {
  static get token(): string {
    return sessionStorage.getItem(WebStorageKeys.tokenKey);
  }

  static set token(value: string) {
    if (!value) {
      sessionStorage.removeItem(WebStorageKeys.tokenKey);
      return;
    }
    sessionStorage.setItem(WebStorageKeys.tokenKey, value);
  }

  static get department(): Department {
    const text = sessionStorage.getItem(WebStorageKeys.departmentKey);
    if (!text) { return null; }
    const obj = JSON.parse(text);
    return this.toDepartment(obj);
  }

  static set department(value: Department) {
    if (!value) {
      sessionStorage.removeItem(WebStorageKeys.departmentKey);
      return;
    }
    const text = JSON.stringify(this.toDepartmentObject(value));
    sessionStorage.setItem(WebStorageKeys.departmentKey, text);
  }

  static get user(): User {
    return this.getUser(WebStorageKeys.userKey);
  }

  static set user(value: User) {
    this.setUser(WebStorageKeys.userKey, value);
  }

  private static getUser(key: string): User {
    const text = sessionStorage.getItem(key);
    if (!text) { return null; }
    const obj = JSON.parse(text);
    return new User(
      obj.id,
      obj.name,
      obj.profession
        ? new Profession(obj.profession.id, obj.profession.name, obj.profession.category)
        : null,
      obj.kanaName,
      obj.email,
      obj.phone,
    );
  }

  private static setUser(key: string, value: User): void {
    if (!value) {
      sessionStorage.removeItem(key);
      return;
    }
    const text = JSON.stringify({
      id: value.id,
      name: value.name,
      profession: value.profession
        ? {
          id: value.profession.id,
          name: value.profession.name,
          category: value.profession.category,
        }
        : null,
      kanaName: value.kanaName,
    });
    sessionStorage.setItem(key, text);
  }

  static get configuration(): IConfiguration {
    const text = sessionStorage.getItem(WebStorageKeys.configurationKey);
    if (!text) { return null; }
    return JSON.parse(text);
  }

  static set configuration(value: IConfiguration) {
    if (!value) {
      sessionStorage.removeItem(WebStorageKeys.configurationKey);
      return;
    }
    sessionStorage.setItem(WebStorageKeys.configurationKey, JSON.stringify(value));
  }

  static get extraConfiguration(): IConfiguration {
    const text = sessionStorage.getItem(WebStorageKeys.extraConfigurationKey);
    if (!text) { return null; }
    return JSON.parse(text);
  }

  static set extraConfiguration(value: IConfiguration) {
    if (!value) {
      sessionStorage.removeItem(WebStorageKeys.extraConfigurationKey);
      return;
    }
    sessionStorage.setItem(WebStorageKeys.extraConfigurationKey, JSON.stringify(value));
  }

  static get userAuthority(): UserAuthority {
    const text = sessionStorage.getItem(WebStorageKeys.userAuthorityKey);
    if (!text) { return null; }
    const obj = JSON.parse(text);
    return new UserAuthority(
      obj.documentEditable,
      obj.documentDeletable,
      obj.documentPrintable,
      obj.formatEditable,
      obj.hierarchiesEditable,
      obj.administratorDepartmentIds,
      obj.isOrderable,
      obj.commonItemEditable,
      obj.maintenanceAccessible,
    );
  }

  static set userAuthority(value: UserAuthority) {
    if (!value) {
      sessionStorage.removeItem(WebStorageKeys.userAuthorityKey);
      return;
    }
    const text = JSON.stringify({
      documentEditable: value.documentEditable,
      documentDeletable: value.documentDeletable,
      documentPrintable: value.documentPrintable,
      formatEditable: value.formatEditable,
      hierarchiesEditable: value.hierarchiesEditable,
      administratorDepartmentIds: value.administratorDepartmentIds,
      isOrderable: value.isOrderable,
      commonItemEditable: value.commonItemEditable,
      maintenanceAccessible: value.maintenanceAccessible,
    });
    sessionStorage.setItem(WebStorageKeys.userAuthorityKey, text);
  }

  static get userDepartments(): Department[] {
    const text = sessionStorage.getItem(WebStorageKeys.userDepartmentsKey);
    if (!text) { return []; }
    const obj = JSON.parse(text);
    if (!obj || !Array.isArray(obj)) { return []; }
    return obj.map(o => Object.freeze(this.toDepartment(o)));
  }

  static set userDepartments(values: Department[]) {
    if (!values) {
      sessionStorage.removeItem(WebStorageKeys.userDepartmentsKey);
      return;
    }
    const text = JSON.stringify(values.map(v => this.toDepartmentObject(v)));
    sessionStorage.setItem(WebStorageKeys.userDepartmentsKey, text);
  }

  private static toDepartmentObject(value: Department): any {
    if (!value) { return null; }
    return {
      id: value.id,
      name: value.name,
      shortName: value.shortName,
      isDeleted: value.isDeleted,
    };
  }

  private static toDepartment(value: any): Department {
    if (!value) { return null; }
    return new Department(
      value.id,
      value.name,
      value.shortName,
      value.isDeleted,
    );
  }

  static get orderUser(): User {
    return this.getUser(WebStorageKeys.orderUserKey);
  }

  static set orderUser(value: User) {
    this.setUser(WebStorageKeys.orderUserKey, value);
  }

  static get attendingUser(): User {
    return this.getUser(WebStorageKeys.attendingUserKey);
  }

  static set attendingUser(value: User) {
    this.setUser(WebStorageKeys.attendingUserKey, value);
  }

  static get clientIpAddress(): string {
    return sessionStorage.getItem(WebStorageKeys.clientIpAddressKey);
  }

  static set clientIpAddress(value: string) {
    if (!value) {
      sessionStorage.removeItem(WebStorageKeys.clientIpAddressKey);
      return;
    }
    sessionStorage.setItem(WebStorageKeys.clientIpAddressKey, value);
  }

  static get isNotRestored(): boolean {
    const value = sessionStorage.getItem(WebStorageKeys.isNotRestoredKey);
    return value
        && (value.toLowerCase() === "true");
  }

  static set isNotRestored(value: boolean) {
    if (!value) {
      sessionStorage.removeItem(WebStorageKeys.isNotRestoredKey);
      return;
    }
    sessionStorage.setItem(WebStorageKeys.isNotRestoredKey, value.toString());
  }

  static get isManualCloseable(): boolean {
    const value = sessionStorage.getItem(WebStorageKeys.isManualCloseableKey);
    return value
        && (value.toLowerCase() === "true");
  }

  static set isManualCloseable(value: boolean) {
    if (value == null) {
      sessionStorage.removeItem(WebStorageKeys.isManualCloseableKey);
      return;
    }
    sessionStorage.setItem(WebStorageKeys.isManualCloseableKey, value.toString());
  }

  static get entryFullPath(): string {
    return sessionStorage.getItem(WebStorageKeys.entryFullPath);
  }

  static set entryFullPath(value: string) {
    if (!value) {
      sessionStorage.removeItem(WebStorageKeys.entryFullPath);
      return;
    }
    sessionStorage.setItem(WebStorageKeys.entryFullPath, value);
  }

  static get entryQueryParameters(): { [key: string]: string } {
    if (!this.entryFullPath) {
      return null;
    }
    const query = this.entryFullPath.split("?");
    if (query.length < 2) {
      return {};
    }
    const parameters = query[1].split("&");
    const results: { [key: string]: string } = {};
    for (const parameter of parameters) {
      const pair = parameter.split("=");
      if (pair.length < 2) {
        continue;
      }
      results[pair[0]] = pair[1];
    }
    return results;
  }

  static get launchedInformation(): ILaunchedInformation {
    return this.readSessionStorageAsJson(WebStorageKeys.launchedInformationKey);
  }

  static set launchedInformation(value: ILaunchedInformation) {
    this.writeSessionStorageAsJson(WebStorageKeys.launchedInformationKey, value);
  }

  private static readSessionStorageAsJson<T>(key: string): T {
    const text = sessionStorage.getItem(key);
    return text
      ? JSON.parse(text) as T
      : null;
  }

  private static writeSessionStorageAsJson<T>(key: string, value: T): void {
    if (!value) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  static get departmentGroup(): DepartmentGroup {
    const text = sessionStorage.getItem(WebStorageKeys.departmentGroupKey);
    if (!text) { return null; }
    const obj = JSON.parse(text);
    return Object.freeze(this.toDepartmentGroup(obj));
  }

  static set departmentGroup(value: DepartmentGroup) {
    if (!value) {
      sessionStorage.removeItem(WebStorageKeys.departmentGroupKey);
      return;
    }
    const text = JSON.stringify(this.toDepartmentGroupObject(value));
    sessionStorage.setItem(WebStorageKeys.departmentGroupKey, text);
  }

  private static toDepartmentGroup(source: any): DepartmentGroup {
    if (!source) { return null; }
    return new DepartmentGroup(
      source.id,
      source.name,
      !!source.departments
        ? source.departments.map((o: any) => Object.freeze(this.toDepartment(o)))
        : [],
      source.isDeleted,
    );
  }
  
  private static toDepartmentGroupObject(value: DepartmentGroup): any {
    if (!value) { return null; }
    return {
      id: value.id,
      name: value.name,
      departments: !!value.departments
        ? value.departments.map(v => this.toDepartmentObject(v))
        : [],
      isDeleted: value.isDeleted,
    };
  }

  static get referenceableDepartmentGroups(): DepartmentGroup[] {
    const text = sessionStorage.getItem(WebStorageKeys.referenceableDepartmentGroupsKey);
    if (!text) { return []; }
    const obj = JSON.parse(text);
    if (!obj || !Array.isArray(obj)) { return []; }
    return obj.map(o => Object.freeze(this.toDepartmentGroup(o))).filter(g => !!g);
  }

  static set referenceableDepartmentGroups(values: DepartmentGroup[]) {
    if (!values) {
      sessionStorage.removeItem(WebStorageKeys.referenceableDepartmentGroupsKey);
      return;
    }
    const text = JSON.stringify(values.map(v => this.toDepartmentGroupObject(v)));
    sessionStorage.setItem(WebStorageKeys.referenceableDepartmentGroupsKey, text);
  }

  static get isDepartmentGroupScopeEnable(): boolean {
    const value = sessionStorage.getItem(WebStorageKeys.isDepartmentGroupScopeEnableKey);
    return value
        && (value.toLowerCase() === "true");
  }

  static set isDepartmentGroupScopeEnable(value: boolean) {
    if (value == null) {
      sessionStorage.removeItem(WebStorageKeys.isDepartmentGroupScopeEnableKey);
      return;
    }
    sessionStorage.setItem(WebStorageKeys.isDepartmentGroupScopeEnableKey, value.toString());
  }

  static get userSelection(): UserSelectionInformation {
    const text = sessionStorage.getItem(WebStorageKeys.userSelectionKey);
    if (!text) { return null; }
    return UserSelectionInformation.fromStoreObject(JSON.parse(text));
  }

  static set userSelection(value: UserSelectionInformation) {
    if (value == null) {
      sessionStorage.removeItem(WebStorageKeys.userSelectionKey);
      return;
    }
    const text = JSON.stringify(value.toStoreObject());
    sessionStorage.setItem(WebStorageKeys.userSelectionKey, text);
  }

  static get postedData(): any {
    const text = sessionStorage.getItem(WebStorageKeys.postedDataKey);
    if (!text) { return null; }
    return JSON.parse(text);
  }

  static set postedData(value: any) {
    if (value == null) {
      sessionStorage.removeItem(WebStorageKeys.postedDataKey);
      return;
    }
    const text = JSON.stringify(value);
    sessionStorage.setItem(WebStorageKeys.postedDataKey, text);
  }
}
